import { get, set } from "idb-keyval";
import { staticFonts } from "pages/flyer/builder/constants";
import { useGoogleFonts } from "pages/flyer/builder/hooks/useGoogleFonts";
import { useEffect, useState } from "react";
import { customFontStorage } from "../services/customFontStorage";
import { s3CustomFontService } from "../services/s3CustomFontService";

export const useAddAllFontsForFB = () => {
  const [fonts, setFonts] = useState<Array<any>>([]);
  const [fontsInfo, setFontsInfo] = useState([]);
  const [customFontsLoaded, setCustomFontsLoaded] = useState(false);
  const [cdnFontsLoaded, setCdnFontsLoaded] = useState(false);

  useEffect(() => {
    const fetchFonts = async () => {
      try {
        // Load cached Google fonts and static fonts
        const cachedFonts = await get("fonts");
        const cachedFontsInfo = await get("fontsInfo");

        let googleFontNames: string[] = [];
        let fontsInfoData: any[] = [];

        if (cachedFonts && cachedFontsInfo) {
          googleFontNames = cachedFonts;
          fontsInfoData = cachedFontsInfo;
        } else {
          const resp = await fetch(
            "https://www.googleapis.com/webfonts/v1/webfonts?key=AIzaSyBXWb1NMG-ztloVwXnOOR7vRdEuE-V2KI4"
          );
          const res = await resp.json();

          googleFontNames = (res.items || []).map((font: any) => font.family);
          const staticFontsNames = Object.keys(staticFonts);
          const allBaseFontsNames = [...googleFontNames, ...staticFontsNames];
          googleFontNames = allBaseFontsNames.sort((a, b) => a.localeCompare(b));

          // Request common weights and italic to ensure visible differences
          const requestedStyles: (string | number)[] = [300, 400, 600, 700, "italic"];
          fontsInfoData = googleFontNames.map((family: string) => ({ family, styles: requestedStyles }));

          await set("fonts", googleFontNames);
          await set("fontsInfo", fontsInfoData);
        }

        let allCustomFontNames: string[] = [];

        // Load S3 CDN fonts first (primary source)
        try {
          const cdnFontNames = await s3CustomFontService.loadFontsIntoDocument();
          allCustomFontNames = [...allCustomFontNames, ...cdnFontNames];
          setCdnFontsLoaded(true);
          console.log(`✅ Loaded ${cdnFontNames.length} custom fonts from S3 CDN`);
        } catch (error) {
          console.error("Failed to load S3 CDN fonts:", error);
          setCdnFontsLoaded(true);
        }

        // Load local fonts as fallback (for backward compatibility)
        try {
          const localCustomFonts = await customFontStorage.loadStoredFontsIntoDocument();
          // Only add local fonts that aren't already loaded from S3 CDN
          const newLocalFonts = localCustomFonts.filter(
            (localFont) => !allCustomFontNames.some((cdnFont) => cdnFont.toLowerCase() === localFont.toLowerCase())
          );
          allCustomFontNames = [...allCustomFontNames, ...newLocalFonts];
          setCustomFontsLoaded(true);

          if (newLocalFonts.length > 0) {
            console.log(`✅ Loaded ${newLocalFonts.length} additional fonts from local storage`);
          }
        } catch (error) {
          console.error("Failed to load local custom fonts:", error);
          setCustomFontsLoaded(true);
        }

        // Combine all font names (Google fonts + static fonts + custom fonts)
        const allFontsNames = [...googleFontNames, ...allCustomFontNames];
        const sortedAllFontsNames = allFontsNames.sort((a, b) => a.localeCompare(b));

        setFonts(sortedAllFontsNames);
        setFontsInfo(fontsInfoData as any); // Keep Google fonts info for font loading
      } catch (error) {
        console.error("Error fetching fonts:", error);
        setCustomFontsLoaded(true);
        setCdnFontsLoaded(true);
      }
    };

    fetchFonts();
  }, []);

  useGoogleFonts(fontsInfo);

  return {
    fonts,
    customFontsLoaded: customFontsLoaded && cdnFontsLoaded,
    addCustomFont: async (fontName: string) => {
      // Add a newly uploaded custom font to the current fonts list
      setFonts((prevFonts) => {
        if (!prevFonts.includes(fontName)) {
          const updatedFonts = [...prevFonts, fontName];
          return updatedFonts.sort((a, b) => a.localeCompare(b));
        }
        return prevFonts;
      });
    },
    removeCustomFont: async (fontName: string) => {
      // Remove a custom font from the current fonts list
      setFonts((prevFonts) => prevFonts.filter((font) => font !== fontName));
    },
  };
};
